import type { Ref } from 'vue';
import { ref } from 'vue';
import { useApi } from '@/services/api/useApi';
import type { CarInformation } from '@/features/HsnTsn/types';
import { currentWkz } from '@/utils/wkz/wkz';
import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';

interface UseHsnTsn {
  isValidHsnTsn: Ref<boolean>;
  belongsToCurrentWKZ: Ref<boolean>;
  revalidateHsnTsnInformation: (
    hsn: string,
    tsn: string,
    isTemporaryLookup?: boolean,
  ) => Promise<CarInformation | null>;
  information: Ref<CarInformation | null>;
  invalidate: () => void;
}

const isValidHsnTsn = ref(true);
const belongsToCurrentWKZ = ref(true);

const information = ref<CarInformation | null>(null);

export const useHsnTsn = (): UseHsnTsn => {
  const revalidateHsnTsnInformation = async (
    hsn: string,
    tsn: string,
    isTemporaryLookup = false,
  ) => {
    if (hsn !== '' && tsn !== '') {
      const [, request] = useApi().vehicleData.getByHsnTsn(hsn, tsn);
      const result = await request;
      if (result === null) {
        throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
          additionalErrorMessage: 'hsnTsn -> getByHsnTsn',
        });
      }

      if (isTemporaryLookup) {
        return result.hsnTsnValid ? result : null;
      }

      isValidHsnTsn.value = result.hsnTsnValid;

      if (isValidHsnTsn.value) {
        information.value = result;
        belongsToCurrentWKZ.value = currentWkz.value === result.wkz;
      } else {
        information.value = null;
        belongsToCurrentWKZ.value = true;
      }

      return result;
    }

    return null;
  };

  const invalidate = (): void => {
    information.value = null;
  };

  return {
    isValidHsnTsn,
    belongsToCurrentWKZ,
    revalidateHsnTsnInformation,
    information,
    invalidate,
  };
};
